import { useState, useEffect } from 'react';
import { cn } from '../../lib/cn';

/**
 * A fixed-position footer toolbar that sits at the bottom of the viewport,
 * offset to account for the TeacherShell sidebar.
 *
 * Usage:
 *   <StickyFooter>
 *     <div className="flex items-center justify-between">...</div>
 *   </StickyFooter>
 */
export default function StickyFooter({ children, className }) {
  const [leftOffset, setLeftOffset] = useState(0);

  useEffect(() => {
    const measure = () => {
      // The sidebar is the first child of the flex container in TeacherShell
      const sidebar = document.querySelector('[data-sidebar]');
      setLeftOffset(sidebar ? sidebar.offsetWidth : 0);
    };

    measure();
    // Re-measure on resize (sidebar collapse/expand)
    window.addEventListener('resize', measure);
    const observer = new MutationObserver(measure);
    const sidebar = document.querySelector('[data-sidebar]');
    if (sidebar) observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {/* Spacer so content isn't hidden behind the fixed footer */}
      <div className="h-16" />
      <div
        className={cn(
          'fixed bottom-0 right-0 z-30',
          'h-16 flex items-center',
          'bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm',
          'border-t border-gray-200 dark:border-gray-700',
          'px-6',
          className
        )}
        style={{ left: leftOffset }}
      >
        {children}
      </div>
    </>
  );
}
